import React, { useEffect, useMemo, useRef, useState } from 'react';
import { applyTypography, useT } from '@repo/common';

/**
 * 볼드 · 글머리 기호만 지원하는 초경량 리치 텍스트 입력.
 * 화면에는 굵게/불릿이 그대로 보이고, 값은 평문 마크업으로 저장된다.
 *   **굵게**  /  줄 앞 "- "
 * 상세 화면의 RichText 가 같은 규칙으로 렌더링하므로 백엔드 변경 없음.
 */

const BULLET_RE = /^\s*(?:[-*•]|\d+[.)])\s+/;

const escapeHtml = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const inlineToHtml = (text) =>
  escapeHtml(text).replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');

/** 평문 마크업 → 에디터 HTML */
export const markupToHtml = (value = '') => {
  const lines = String(value).replace(/\r\n/g, '\n').split('\n');
  let html = '';
  let inList = false;
  lines.forEach((line) => {
    if (BULLET_RE.test(line)) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li>${inlineToHtml(line.replace(BULLET_RE, '')) || '<br>'}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<div>${inlineToHtml(line) || '<br>'}</div>`;
    }
  });
  if (inList) html += '</ul>';
  return html;
};

const isBoldEl = (el) => {
  const tag = el.tagName;
  if (tag === 'B' || tag === 'STRONG') return true;
  const w = el.style?.fontWeight;
  return w === 'bold' || w === 'bolder' || Number(w) >= 600;
};

/** 인라인 노드들 → "**굵게**" 텍스트 */
const inlineText = (node, bold = false) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue.replace(/ /g, ' ');
    return bold && text.trim() ? `**${text}**` : text;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  if (node.tagName === 'BR') return '';
  const childBold = bold || isBoldEl(node);
  return Array.from(node.childNodes)
    .map((c) => inlineText(c, childBold))
    .join('')
    // 인접한 **a****b** → **ab**
    .replace(/\*\*\*\*/g, '');
};

const BLOCK_TAGS = new Set(['DIV', 'P', 'UL', 'OL', 'LI', 'BR']);

/** 에디터 DOM → 평문 마크업 */
export const htmlToMarkup = (root) => {
  const lines = [];
  let current = '';
  let hasCurrent = false;

  const flush = () => {
    lines.push(current);
    current = '';
    hasCurrent = false;
  };

  const walk = (node, inLi) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += inlineText(node);
      hasCurrent = true;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = node.tagName;

    if (tag === 'BR') {
      // 블록 안에서 유일한 <br> 은 빈 줄 표현이므로 줄바꿈으로 세지 않는다
      const onlyChild =
        node.parentNode?.childNodes.length === 1 &&
        BLOCK_TAGS.has(node.parentNode.tagName);
      if (!onlyChild) flush();
      else hasCurrent = true;
      return;
    }
    if (tag === 'UL' || tag === 'OL') {
      if (hasCurrent) flush();
      Array.from(node.childNodes).forEach((c) => walk(c, true));
      return;
    }
    if (tag === 'LI') {
      if (hasCurrent) flush();
      current = '- ';
      hasCurrent = true;
      Array.from(node.childNodes).forEach((c) => {
        if (c.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(c.tagName)) {
          walk(c, true);
        } else {
          current += inlineText(c);
        }
      });
      flush();
      return;
    }
    if (tag === 'DIV' || tag === 'P') {
      if (hasCurrent) flush();
      Array.from(node.childNodes).forEach((c) => walk(c, inLi));
      flush();
      return;
    }
    // 그 외 인라인 (b, span, ...)
    current += inlineText(node);
    hasCurrent = true;
  };

  Array.from(root.childNodes).forEach((c) => walk(c, false));
  if (hasCurrent) flush();

  return lines
    .map((l) => l.replace(/\*\*\*\*/g, '').replace(/^- $/, '- '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\n+$/, '');
};

/** 글자수는 마크업 기호 제외한 실제 텍스트 기준 */
export const plainLength = (markup = '') =>
  markup.replace(/\*\*/g, '').length;

const RichTextarea = ({
  id,
  label,
  placeholder,
  value = '',
  onChange,
  maxLength,
  state = 'default',
  message,
  minHeight = '168px',
  validationField,
}) => {
  const t = useT();
  const editorRef = useRef(null);
  const lastEmitted = useRef(value);
  const [focused, setFocused] = useState(false);
  const [bold, setBold] = useState(false);
  const [list, setList] = useState(false);

  // 최초 마운트, 그리고 외부에서 값이 바뀐 경우(수정 화면 로드, 임시저장 복원)에만 DOM 갱신.
  // 사용자가 타이핑한 값은 lastEmitted 와 같으므로 건너뛰어 커서가 튀지 않는다.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastEmitted.current || el.innerHTML === '') {
      el.innerHTML = markupToHtml(value);
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = () => {
    if (!editorRef.current) return;
    const next = htmlToMarkup(editorRef.current);
    lastEmitted.current = next;
    onChange?.(next);
  };

  const syncToolbar = () => {
    try {
      setBold(document.queryCommandState('bold'));
      setList(document.queryCommandState('insertUnorderedList'));
    } catch {
      /* noop */
    }
  };

  const exec = (command) => (e) => {
    e.preventDefault(); // 에디터 포커스 유지
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    emit();
    syncToolbar();
  };

  const handleBeforeInput = (e) => {
    if (!maxLength) return;
    const inputType = e.nativeEvent?.inputType || '';
    if (!inputType.startsWith('insert')) return;
    const incoming = e.nativeEvent?.data?.length ?? 1;
    const current = plainLength(lastEmitted.current);
    const sel = window.getSelection();
    const replaced = sel && !sel.isCollapsed ? sel.toString().length : 0;
    if (current - replaced + incoming > maxLength) e.preventDefault();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let text = e.clipboardData.getData('text/plain');
    if (maxLength) {
      const room = maxLength - plainLength(lastEmitted.current);
      text = text.slice(0, Math.max(0, room));
    }
    document.execCommand('insertText', false, text);
    emit();
  };

  const isFailed = state === 'failed';
  const borderClass = isFailed
    ? 'border-functional-failed03 focus-within:border-functional-failed03 focus-within:ring-functional-failed03/20'
    : 'border-grayscale-gray02 focus-within:border-green-deep02 focus-within:ring-green-deep02/20';

  const count = useMemo(() => plainLength(value), [value]);
  const isEmpty = !value;

  const btnBase =
    'typo-b13 min-w-7 h-7 px-2 rounded-md border select-none transition-colors';
  const btnClass = (active) =>
    `${btnBase} ${
      active
        ? 'bg-grayscale-gray07 text-grayscale-gray00 border-grayscale-gray07'
        : 'bg-grayscale-gray00 text-text-secondary border-grayscale-gray02 hover:bg-grayscale-gray01'
    }`;

  return (
    <div className='w-full' data-validation-field={validationField}>
      <label
        htmlFor={id}
        className='block text-text-primary typo-b12Strong tabletH:typo-b14Strong mb-2'
      >
        {label}
      </label>

      <div className='flex items-center gap-1.5 mb-1.5'>
        <button
          type='button'
          className={`${btnClass(bold)} font-bold`}
          onMouseDown={exec('bold')}
          title={t('Bold', '굵게')}
          aria-label={t('Bold', '굵게')}
          aria-pressed={bold}
        >
          B
        </button>
        <button
          type='button'
          className={btnClass(list)}
          onMouseDown={exec('insertUnorderedList')}
          title={t('Bullet list', '글머리 기호')}
          aria-label={t('Bullet list', '글머리 기호')}
          aria-pressed={list}
        >
          •≡
        </button>
      </div>

      <div
        className={`relative border box-border rounded-[12px] bg-grayscale-gray00 ${borderClass} focus-within:ring-2 transition-[border-color,box-shadow] duration-200 cursor-text`}
        style={{ minHeight }}
        onClick={() => editorRef.current?.focus()}
      >
        {isEmpty && !focused && (
          <div
            className={`absolute left-4 top-3 right-4 pointer-events-none text-text-placeholder ${applyTypography('b16', placeholder)}`}
          >
            {placeholder}
          </div>
        )}
        <div
          id={id}
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role='textbox'
          aria-multiline='true'
          aria-label={label}
          className={`job-rich-editor w-full outline-none px-4 py-3 text-text-primary ${applyTypography('b16', value)} [&_b]:font-semibold [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5`}
          style={{ minHeight, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          onInput={emit}
          onBeforeInput={handleBeforeInput}
          onPaste={handlePaste}
          onKeyUp={syncToolbar}
          onMouseUp={syncToolbar}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            emit();
          }}
        />
      </div>

      <div
        className={`relative flex justify-between items-start ${
          message || maxLength ? 'mt-2' : 'mt-0'
        }`}
      >
        {message && (
          <span
            className={`inline-flex items-center ${
              isFailed ? 'text-text-failed' : 'text-text-secondary'
            } ${applyTypography('b12', message)}`}
          >
            {message}
          </span>
        )}
        {maxLength && (
          <span
            className={`${applyTypography('b12', `${count}/${maxLength}`)} absolute right-0 text-text-secondary`}
          >
            {count}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
};

export default RichTextarea;
